"use client";

import { useCallback, useEffect, useState } from "react";
import { i18n } from "./instance";
import { useLanguage } from "./use-language";

type I18NOpts = Parameters<ReturnType<typeof i18n.getFixedT>>;

/**
 * Client-side translation hook.
 *
 * Uses `i18n.getFixedT(language, ns)` to create a `t` function pinned to the
 * current language and namespace. This avoids race conditions during language
 * switches that can occur with `useTranslation()` from react-i18next.
 *
 * @param ns - The translation namespace (e.g. "ui", "errors", "inputs", "features")
 */
export function useT(ns = "ui") {
  const { language } = useLanguage();
  const [, setTick] = useState(0);

  useEffect(() => {
    if (i18n.resolvedLanguage !== language) {
      i18n.changeLanguage(language).then(() => {
        setTick((prev) => prev + 1);
      });
    }
  }, [language]);

  const t = useCallback(
    (...options: I18NOpts) => {
      return i18n.getFixedT(language, ns)(...options);
    },
    [language, ns],
  );

  return { t, i18n, ready: i18n.isInitialized };
}
